import { formatCurrency } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Inject,
    Input, LOCALE_ID,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { buildProviders, KalAutocompleteOption, KalDate, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import cloneDeep from 'lodash-es/cloneDeep';
import sortBy from 'lodash-es/sortBy';
import { forkJoin, merge, Observable, of } from 'rxjs';
import { filter, startWith, switchMap, tap } from 'rxjs/operators';
import { CURRENCY } from 'src/app/_core/core.module';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { SharedBasePaymentMethods } from 'src/app/_shared/classes/settlements/shared-base-payment-methods';
import { SharedRepaymentSchedule } from 'src/app/_shared/classes/settlements/shared-repayment-schedule';
import {
    SharedPaymentMethodsService
} from 'src/app/_shared/components/settlements/shared-payment-methods/shared-payment-methods.service';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    IbanEditDialogAction,
    IbanEditDialogComponent,
    IbanEditDialogData
} from 'src/app/_shared/dialogs/iban-edit-dialog/iban-edit-dialog.component';
import {
    SharedEditRepaymentScheduleDialogComponent,
    SharedEditRepaymentScheduleDialogData
} from 'src/app/_shared/dialogs/settlements/shared-edit-repayment-schedule-dialog/shared-edit-repayment-schedule-dialog.component';
import {
    SharedAdvancedSettlementState
} from 'src/app/_shared/stores/settlements/shared-advanced-settlement/shared-advanced-settlement.state';
import {
    SharedPaymentMethodsGetIbansListAction
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.action';
import {
    SharedPaymentMethodsState
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.state';
import { UserState } from 'src/app/_user/store/user/user.state';
import { MoyensPaiement, TypeJournal, TypeReglement } from 'src/app/board/activity/activity.constants';
import { arrayNotEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { getISODate, getKalDateFromObject } from 'src/app/utils/date.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { applicationCodes } from 'src/environments/application-codes';
import {
    CompteBancaireDto,
    CompteCourantADebiterOuCrediterModelDto,
    CompteDto,
    CritereRechercheNumeroPieceDtoInput,
    CriteresCompteDtoInput,
    CriteresRibDtoInput,
    FactureDto,
    JournalDto,
    MoyenPaiementDto,
    ReglementModelDtoInput,
    RibDto,
    RibMandatDto
} from 'src/graphql/generated-types';

const ChequeNumberSize = 20;

type AutocompleteBankDataSourceType = KalAutocompleteOption<string>;

@Component({
    selector: 'app-shared-payment-methods',
    templateUrl: './shared-payment-methods.component.html',
    styleUrls: ['./shared-payment-methods.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [...buildProviders(SharedPaymentMethodsComponent), SharedPaymentMethodsService],
    standalone: false
})
export class SharedPaymentMethodsComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    @Input() paymentType: TypeReglement = TypeReglement.Beneficiaire;

    @Input() accountId: number | null = null;

    @Input() datasource: SharedBasePaymentMethods | null = null;

    paymentMethodsList$: Observable<MoyenPaiementDto[]> = inject(Store).select(SharedPaymentMethodsState.paymentMethodsList);

    journalsList$: Observable<JournalDto[]> = inject(Store).select(SharedPaymentMethodsState.journalsList);

    banksList$: Observable<string[]> = inject(Store).select(SharedPaymentMethodsState.banksList);

    ibansList$: Observable<RibDto[]> = inject(Store).select(SharedPaymentMethodsState.ibansList);

    invoicesList$: Observable<FactureDto[]> = inject(Store).select(SharedAdvancedSettlementState.invoicesList);

    defaultCollectiveAccountId$: Observable<number | null> = inject(Store).select(SharedPaymentMethodsState.defaultCollectifAccountId);

    form: FormGroup;

    journalsList: JournalDto[] = [];

    pmvCcfAccountsList: CompteCourantADebiterOuCrediterModelDto[] = [];

    partNumberCriterias: CritereRechercheNumeroPieceDtoInput = {
        IdJournal: null,
        DateEcriture: getISODate(new KalDate())
    };

    ibansList: RibDto[] = [];

    paymentMethodsList: MoyenPaiementDto[] = [];

    bankAccountsList: CompteBancaireDto[] = [];

    depositCollectiveAccountsList: CompteDto[] = [];

    collectiveAccountsList: CompteDto[] = [];

    invoicesList: FactureDto[] = [];

    autocompleteBankDataSource: AutocompleteBankDataSourceType[] = [];

    constructor(private readonly fb: FormBuilder,
                private readonly dialogService: KalDialogService,
                private readonly activityPaymentMethodsService: SharedPaymentMethodsService,
                @Inject(LOCALE_ID) private readonly locale: string,
                @Inject(CURRENCY) private readonly currency: string,
                protected readonly cdr: ChangeDetectorRef) {
        super(cdr);
        this.startLoadingZone('datasource');
    }

    private _maxAmount: number;

    get maxAmount(): number {
        return this._maxAmount;
    }

    @Input()
    set maxAmount(amount: number) {
        this._maxAmount = amount || amount === 0 ? amount : null;

        if (this.form) {
            const amountControl = this.form.get('amount');
            amountControl.patchValue(this._maxAmount, {emitEvent: false});

            // Lorsque le montant maximum est modifié après initialisation du formulaire, on doit modifier les validateurs
            amountControl.clearValidators();

            const validators = [FormValidator.nonNullAmount];
            if (this.isDepositContext || this.isRefundContext || this.isSupplierContext) {
                validators.push(Validators.max(this.maxAmount));
            }
            amountControl.setValidators(validators);
            amountControl.updateValueAndValidity();
        }

        this.cdr.markForCheck();
    }

    get ibanIdControl(): FormControl {
        return this.form.get('ibanId') as FormControl;
    }

    // <editor-fold defaultstate="collapsed" desc="Displayed lists">

    get filtetedJournalsList(): JournalDto[] {
        return this.journalsList.filter(j => {
            if (!!this.selectedPaymentMethod) {
                if (this.selectedPaymentMethod.IsFiduciaire) {
                    return j.TypeJournal.Code === TypeJournal.EspecesEtAutres;
                }

                if ([MoyensPaiement.CompteCourantFournisseur, MoyensPaiement.PorteMonnaieVirtuel].every(e => e !== this.selectedPaymentMethod.TypeMoyenPaiement.Code)) {
                    return j.TypeJournal.Code === TypeJournal.Tresorerie;
                }
            }

            return j.TypeJournal.Code === TypeJournal.OuvrantDroit;
        });
    }

    get filteredMandatsList(): RibMandatDto[] {

        const ibansMandatsList = (this.ibansList || []).map(e => e?.ListeRibMandat?.RibMandatDTO || []);

        const mandatsList: RibMandatDto[] = ibansMandatsList.reduce((a: RibMandatDto[], b: RibMandatDto[]) => {
            return a.concat(b);
        }, []);

        const ibanId = this.ibanIdControl.value;
        const bankAccountId = this.form.value.bankAccountId;

        let filteredMandatsList = [];
        if (bankAccountId) {
            filteredMandatsList = mandatsList.filter((m: RibMandatDto) => !m.IsClos && m.Rib.Id === ibanId && m.CompteBancaire.Id === bankAccountId);
        }

        return filteredMandatsList;
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Handle lists display">

    get hasSelectedCheque(): boolean {
        return this.selectedPaymentMethod?.TypeMoyenPaiement?.Code === MoyensPaiement.ChequeBancaire;
    }

    get hasSelectedBankTransfert(): boolean {
        return !!this.selectedPaymentMethod && [
            MoyensPaiement.PrelevementSEPASurCompte,
            MoyensPaiement.VirementSEPASurCompte
        ].some(e => e === this.selectedPaymentMethod.TypeMoyenPaiement.Code);
    }

    get hasSelectedWithdrawal(): boolean {
        return this.selectedPaymentMethod.TypeMoyenPaiement.Code === MoyensPaiement.PrelevementSEPASurCompte;
    }

    get shouldDisplayBankAccount(): boolean {
        return this.selectedPaymentMethod && [
            MoyensPaiement.Especes,
            MoyensPaiement.PorteMonnaieVirtuel,
            MoyensPaiement.CompteCourantFournisseur,
        ].every(e => {
            return e !== this.selectedPaymentMethod.TypeMoyenPaiement.Code;
        });
    }

    get shouldDisplayCollectiveDepositAccount(): boolean {
        return this.isDepositClient || this.isDepositSupplier;
    }

    get shouldDisplayCollectiveAccount(): boolean {
        const paymentMethodCode = this.selectedPaymentMethod?.TypeMoyenPaiement?.Code || null;
        const isCompteCourantFournisseur = (paymentMethodCode === MoyensPaiement.CompteCourantFournisseur);

        return !this.isDepositClient && !(this.isDepositSupplier && !isCompteCourantFournisseur);
    }

    get shouldDisplayPmvCcfAccount(): boolean {
        return this.selectedPaymentMethod
            && [MoyensPaiement.PorteMonnaieVirtuel, MoyensPaiement.CompteCourantFournisseur].some(e => e === this.selectedPaymentMethod?.TypeMoyenPaiement?.Code);
    }

    get shouldDisplayPartNumber(): boolean {
        const parameter = this.snapshot(UserState.applicationParameters).find(p => p.CodeChamp === applicationCodes.NUMPIECE_AUTO);
        return parameter && parameter.CodeValeur === '1';
    }

    get canEditIban(): boolean {
        const ibanParameter = this.snapshot(UserState.applicationParameters).find(p => p.CodeChamp === applicationCodes.EDITION_IBAN_BLOQUEE);
        return ibanParameter && ibanParameter.CodeValeur === '0';
    }

    get hasPaymentMethods(): boolean {
        return arrayNotEmpty(this.paymentMethodsList);
    }

    // </editor-fold>

    get selectPmvCcfAccount(): CompteCourantADebiterOuCrediterModelDto {
        const accountId = this.form.value.pmvCcfAccountId;
        return this.pmvCcfAccountsList.find(a => a.IdCompte === accountId);
    }

    get hasNotEnoughSoldeOnPmvCcf(): boolean {
        return this.selectPmvCcfAccount?.Solde < this.form.value.amount;
    }

    get labelOfPaymentMethodLabel(): string {
        const isDeposit = this.shouldDisplayCollectiveDepositAccount;
        return `Libellé ${isDeposit ? 'de l\'acompte' : 'du règlement'}`;
    }

    get canUseRepaymentSchedule(): boolean {
        const isPaymentTypeAllowed = [
            TypeReglement.AcompteFournisseur,
            TypeReglement.AcompteClient,
            TypeReglement.RemboursementAvoirClient,
            TypeReglement.Fournisseur,
            TypeReglement.RemboursementAvoirFournisseur
        ].every(e => e !== this.paymentType);

        const isPaymentMethodAllowed = [
            MoyensPaiement.PrelevementSEPASurCompte,
            MoyensPaiement.ChequeBancaire,
            MoyensPaiement.PrelevementSurSalaire
        ].some(e => e === this.selectedPaymentMethod.TypeMoyenPaiement.Code);

        return !!this.selectedPaymentMethod
            && isPaymentTypeAllowed
            && isPaymentMethodAllowed;
    }

    get isChequeNumberRequired(): boolean {
        return this.snapshot(UserState.applicationParameters).find(p => p.CodeChamp === 'CNUMCH')?.CodeValeur === '1';
    }

    get updatedOrderLine(): ReglementModelDtoInput {
        const formValues = this.form.value;

        const input: ReglementModelDtoInput = {
            Libelle: formValues.label,
            MontantReglement: formValues.amount,
            DateReglement: getISODate(formValues.date),
            IdJournal: formValues.journalId,
            Journal: this.snapshot(SharedPaymentMethodsState.journalsList).find(j => j.Id === formValues.journalId) || {Id: formValues.journalId},
            CompteAcompteCollectif: this.depositCollectiveAccountsList.find(ca => ca.Id === formValues.depositAccountId) || {Id: 0},
            CompteCollectif: this.collectiveAccountsList.find(ca => ca.Id === formValues.collectiveAccountId) || {Id: 0},
            IdAcompte: 0,
            MoyenPaiement: {
                Libelle: this.selectedPaymentMethod.Libelle,
                Id: this.selectedPaymentMethod.Id,
                IsFiduciaire: this.selectedPaymentMethod.IsFiduciaire,
                Article: this.selectedPaymentMethod.Article,
                Code: this.selectedPaymentMethod.Code,
                TypeMoyenPaiement: {
                    Code: this.selectedPaymentMethod.TypeMoyenPaiement.Code
                }
            }
        };

        if (this.shouldDisplayPartNumber) {
            input.NumPiece = '' + formValues.partNumber;
        } else {
            input.NumPiece = '';
        }

        if (this.shouldDisplayCollectiveAccount) {
            input.CompteCollectif = this.collectiveAccountsList.find(ca => ca.Id === formValues.collectiveAccountId);

            if (!input.CompteCollectif) {
                input.CompteCollectif = {Id: formValues.collectiveAccountId}
            }
        } else if (this.isDepositSupplier || this.isDepositClient) {
            // Historiquement sur G+, c'était le champ "Compte collectif" qui était utilisé pour le remboursement d'un acompte
            // Sur Expert : C'est le rôle du champ "Collectif acompte" désormais
            input.CompteCollectif = {Id: formValues.depositAccountId};
        }

        const codeMP = this.selectedPaymentMethod?.TypeMoyenPaiement?.Code;
        const isPMV  = codeMP === MoyensPaiement.PorteMonnaieVirtuel;
        const isCCF  = codeMP === MoyensPaiement.CompteCourantFournisseur;

        // #59228: Pour PMV/CCF, on utilise toujours le compte CCF/PMV sélectionné (407 ou PMV)
        // même en contexte d'acompte - le CompteAcompteCollectif (409/419) est assigné séparément
        if (isPMV || isCCF) {
            input.IdCompteReglement = formValues.pmvCcfAccountId ?? null;
        } else if (this.hasSelectedCheque) {
            Object.assign(input, {
                NumCheque: formValues.chequeNumber,
                NomTire: formValues.tiretName,
                Banque: formValues.bankName,
                DateEcheance: getISODate(formValues.chequeDepositDate)
            });
        } else if (this.hasSelectedBankTransfert) {
            const selectedMandat = this.filteredMandatsList.find(m => m.Id === formValues.mandatId);
            let mandatList = [];

            if (selectedMandat) {
                mandatList = [{
                    Id: selectedMandat.Id,
                    IsClos: selectedMandat.IsClos
                }];
                Object.assign(input, {
                    RibMandat: {
                        Id: selectedMandat.Id,
                        NumeroReference : selectedMandat.NumeroReference
                    }
                }
            );
            }

            Object.assign(input, {
                    Rib: {
                        Id: formValues.ibanId,
                        ListeRibMandat: {
                            RibMandatDTO: mandatList
                        }
                    }
                }
            );
        }

        if (this.shouldDisplayBankAccount) {
            input.CompteBancaire = {
                Id: formValues.bankAccountId
            };
        }

        if (this.canUseRepaymentSchedule && !!formValues.repaymentSchedule) {
            const {
                paymentStartDate,
                paymentFrequency,
                numberOfDueDate,
                paymentDayOfMonth,
                schedule
            } = formValues.repaymentSchedule;

            input.Echeancier = {
                DateDebutEcheance: getISODate(paymentStartDate),
                Frequence: paymentFrequency,
                NomBanque: formValues.bankName,
                IdCompteBancaire: formValues.bankAccountId,
                IdMoyenPaiement: this.selectedPaymentMethod.Id,
                NbEcheance: numberOfDueDate,
                JourEcheance: paymentDayOfMonth,
                LibelleEcheancier: formValues.label,
                IdBenef: this.datasource.third.Id,
                ListeEcheances: {
                    EcheanceModelDTO: schedule.map(dueDate => {
                        return {
                            ...dueDate,
                            NumeroCheque: dueDate.NumeroCheque ? '' + dueDate.NumeroCheque : '',
                            DateEcheance: getISODate(dueDate.DateEcheance)
                        };
                    })
                },
            };
        }

        return input;
    }

    get overPayment(): number {
        // La Gestion du Trop-Perçu ne concerne que les règlements bénéficiaires
        if (this.paymentType !== TypeReglement.Beneficiaire) {
            return 0;
        }

        return this.maxAmount || this.maxAmount === 0 ? this.form.value.amount - this.maxAmount : 0;
    }

    get repaymentSchedule(): SharedRepaymentSchedule {
        return this.form.value.repaymentSchedule;
    }

    public get selectedPaymentMethod(): MoyenPaiementDto {
        return this.snapshot(SharedPaymentMethodsState.paymentMethodsList)?.find(m => m.Code === this.form.value.paymentMethodCode) || null;
    }

    public get isClientContext(): boolean {
        return [
            TypeReglement.DonCEBenef,
            TypeReglement.ReglementPretBenef,
            TypeReglement.Beneficiaire,
            TypeReglement.AcompteClient,
            TypeReglement.RemboursementAvoirClient,
            TypeReglement.RemboursementPretBenef,
        ].some(e => this.paymentType === e);
    }

    private get isSupplierContext(): boolean {
        return [
            TypeReglement.Fournisseur,
            TypeReglement.AcompteFournisseur,
            TypeReglement.RemboursementAvoirFournisseur
        ].some(e => this.paymentType === e);
    }

    private get isDepositClient(): boolean {
        return this.paymentType === TypeReglement.AcompteClient;
    }

    private get isDepositSupplier(): boolean {
        return this.paymentType === TypeReglement.AcompteFournisseur;
    }

    private get isDepositContext(): boolean {
        return [TypeReglement.AcompteFournisseur, TypeReglement.AcompteClient].some(t => t === this.paymentType);
    }

    private get isRefundContext(): boolean {
        return [TypeReglement.RemboursementAvoirFournisseur, TypeReglement.RemboursementAvoirClient].some(t => t === this.paymentType);
    }

    private get minDueDate(): KalDate {
        if (arrayNotEmpty(this.invoicesList)) {
            return getKalDateFromObject(sortBy(this.invoicesList, i => i.DateFacture)[0], 'DateFacture');
            // recherche de la date la plus ancienne
        }
        return new KalDate();
    }

    patchAutocompletedBank(bank: AutocompleteBankDataSourceType): void {
        this.form.get('bankName').patchValue(bank.value);
    }

    validate(c: AbstractControl): Observable<null | { invalidentry: boolean }> {
        return of(this.form.valid ? null : {invalidentry: true});
    }

    resetAmount(): void {
        const amountControl = this.form.get('amount');
        amountControl.patchValue(this.maxAmount, {emitEvent: false});
        amountControl.updateValueAndValidity();
    }

    openEditIbanDialog({isCreation}: { isCreation: boolean }): void {
        const config = new KalDialogConfig<IbanEditDialogData>({
            title: (isCreation ? 'Ajouter' : 'Editer') + ' un IBAN',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                beneficiaryId: this.isClientContext ? this.datasource.third.Id : null,
                supplierId: this.isSupplierContext ? this.datasource.third.Id : null,
                ibanId: isCreation ? null : this.form.value.ibanId,
                action: IbanEditDialogAction.SaveApiCall
            }
        });

        const dialogRef = this.dialogService.open(IbanEditDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => !!result && result.accepted),
            filter(() => this.isSupplierContext || this.isClientContext),
            switchMap(() => {
                const criterias: CriteresRibDtoInput = {
                    IsClos: false,
                    TakeRibMandat: true
                };

                if (this.isSupplierContext) {
                    criterias.IdFour = this.datasource.third.Id;
                } else if (this.isClientContext) {
                    criterias.IdBenef = this.datasource.third.Id;
                }

                return this.dispatch$(new SharedPaymentMethodsGetIbansListAction(criterias));
            }),
            tap(() => this.setDefaultIban())
        );
    }

    openRepaymentScheduleDialog(): void {
        const [dialogTitleLabel, repaymentSchedule]: [string, SharedRepaymentSchedule] =
            !!this.repaymentSchedule
            ? ['Modification', cloneDeep(this.repaymentSchedule)]
            : ['Création', null];

        const config = new KalDialogConfig<SharedEditRepaymentScheduleDialogData>({
            title: `${dialogTitleLabel} d\'un échéancier`,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                title: this.form.value.label,
                paymentMethod: this.selectedPaymentMethod,
                activityId: this.datasource.activityId,
                amount: this.form.value.amount,
                paymentStartDate: this.form.value.date,
                repaymentSchedule,
                IdBenef: this.datasource.third.Id,
                invoicesList: this.invoicesList,
            }
        });

        const dialogRef = this.dialogService.open(SharedEditRepaymentScheduleDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => !!result && result.accepted),
            tap((result: SharedEditRepaymentScheduleDialogData) => {
                this.form.patchValue({
                    repaymentSchedule: result.repaymentSchedule,
                    label: result.title,
                    bankName: result.repaymentSchedule.bankName,
                    transactionName: result.repaymentSchedule.transactionName,
                    chequeNumber: '',
                });

                this.form.get('chequeNumber').disable();
                this.setValidators();
                this.form.updateValueAndValidity();
            })
        );
    }

    removeRepaymentSchedule(): void {
        this.form.patchValue({repaymentSchedule: null});

        this.form.get('chequeNumber').enable();
        this.setValidators();
        this.form.updateValueAndValidity();
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable({emitEvent: false});
        } else {
            this.form.enable({emitEvent: false});
        }

        this.cdr.markForCheck();
    }

    private updateForm(): void {
        const lastSelectedBankName = this.snapshot(SharedPaymentMethodsState.lastSelectedBankName);
        const defaultBankAccountId = this.snapshot(SharedPaymentMethodsState.defaultBankAccountId);
        const depositAccountId = this.snapshot(SharedPaymentMethodsState.depositAccountId);

        this.form.patchValue({
            bankName: lastSelectedBankName || null,
            bankAccountId: defaultBankAccountId || null,
            depositAccountId: depositAccountId || null,
            label: this.datasource.accountingEntryLabel
        }, {emitEvent: false});

        this.setDefaultIban();
        this.setDefaultCollectiveAccount();
        this.form.updateValueAndValidity();

    }

    private setValidators(): void {
        this.resetValidators();

        if (this.shouldDisplayBankAccount) {
            this.form.get('bankAccountId').setValidators(FormValidator.isNotNull);
        }

        if (this.hasSelectedCheque) {
            if (this.isChequeNumberRequired && !this.repaymentSchedule) {
                this.form.get('chequeNumber').setValidators([FormValidator.isNotNull, Validators.maxLength(ChequeNumberSize)]);
            } else {
                // Si le numéro de chèque n'est pas nécessaire, il faut tout de même remettre le validateur sur sa taille maximum
                this.form.get('chequeNumber').setValidators([Validators.maxLength(ChequeNumberSize)]);
            }
        }

        if (this.shouldDisplayCollectiveAccount) {
            this.form.get('collectiveAccountId').setValidators(FormValidator.isNotNull);
            this.form.get('collectiveAccountId').updateValueAndValidity({emitEvent: false});
        }

        if (this.shouldDisplayPmvCcfAccount) {
            this.form.get('pmvCcfAccountId').setValidators(FormValidator.isNotNull);
        }
    }

    private resetValidators(): void {
        ['bankAccountId', 'chequeNumber', 'pmvCcfAccountId', 'collectiveAccountId', 'ibanId'].forEach(controlName => {
            const control = this.form.get(controlName);
            control.clearValidators();
            control.updateValueAndValidity({emitEvent: false});
        });
    }

    // <editor-fold defaultstate="collapsed" desc="Journal">

    private setDefaultJournal(): Observable<number> {
        if (!this.selectedPaymentMethod) {
            return of(null);
        }
        if (this.selectedPaymentMethod.IsFiduciaire) {
            return this.activityPaymentMethodsService.getDefaultJournalIdByPaymentMethodId(this.selectedPaymentMethod.Id).pipe(
                tap(journalId => this.form.get('journalId').patchValue(journalId))
            );
        } else if (this.selectedPaymentMethod.TypeMoyenPaiement.Code === MoyensPaiement.PorteMonnaieVirtuel) {
            const journal = this.journalsList.find(j => j.TypeBudget === 'S' && j.TypeJournal.Code === TypeJournal.OuvrantDroit);
            if (journal) {
                this.form.get('journalId').patchValue(journal.Id);
            }
            return of(journal?.Id || null);
        } else if(this.selectedPaymentMethod.TypeMoyenPaiement.Code === MoyensPaiement.CompteCourantFournisseur) {
            this.form.get('journalId').reset(null);
            return of(null);
        } else {
            const bankAccountId = this.form.value.bankAccountId;

            if (bankAccountId) {
                return this.activityPaymentMethodsService.getDefaultJournalIdByBankIdAndPaymentMethodId(bankAccountId, this.selectedPaymentMethod.Id).pipe(
                    tap(journalId => this.form.get('journalId').patchValue(journalId))
                );
            }
            return of(null);
        }
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Ibans">

    private setDefaultIban(): void {
        const favoriteIban = this.ibansList.find(iban => iban.EstRibParDefaut);

        if (favoriteIban || arrayOnlyOne(this.ibansList)) {
            this.ibanIdControl.patchValue(favoriteIban?.Id || this.ibansList[0].Id, {emitEvent: false});
        }

        if (this.selectedPaymentMethod?.TypeMoyenPaiement?.Code !== MoyensPaiement.PrelevementSEPASurCompte) {
            return;
        }

        this.setDefaultMandat();
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Mandats">

    private setDefaultMandat(): void {
        if (this.selectedPaymentMethod.TypeMoyenPaiement.Code !== MoyensPaiement.PrelevementSEPASurCompte) {
            return;
        }

        this.form.get('mandatId').patchValue(arrayOnlyOne(this.filteredMandatsList) ? this.filteredMandatsList[0].Id : null);
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Subscriptions">

    private handlePaymentMethodChange(): Observable<any> {
        const actions: Observable<any>[] = [];

        this.form.patchValue({repaymentSchedule: null});
        this.form.get('chequeNumber').enable();
        this.setValidators();
        actions.push(this.setDefaultJournal());
        this.setDefaultIban();

        const shouldDisplayAnyCollective = this.shouldDisplayCollectiveAccount || this.shouldDisplayCollectiveDepositAccount;
        if (this.shouldDisplayPmvCcfAccount || shouldDisplayAnyCollective) {
            if (shouldDisplayAnyCollective) {
                const baseCriterias: CriteresCompteDtoInput = {
                    IsBlocked: false,
                    IsClotured: false,
                    IsCollectifClient: this.isClientContext && !this.isDepositClient,
                    IsCollectifAcompteClient: this.isDepositClient
                };

                if (this.shouldDisplayCollectiveAccount) {
                    actions.push(this.activityPaymentMethodsService.getGenericAccountsList({
                        ...baseCriterias,
                        IsCollectifFournisseur: this.isSupplierContext,
                    }).pipe(
                        tap(collectiveAccountList => {
                            this.collectiveAccountsList = collectiveAccountList;
                            this.setDefaultCollectiveAccount();
                        })
                    ));
                }

                if (this.shouldDisplayCollectiveDepositAccount) {
                    actions.push(this.activityPaymentMethodsService.getGenericAccountsList({
                        ...baseCriterias,
                        IsCollectifAcompteFour: this.isDepositSupplier
                    }).pipe(
                        tap(depositCollectiveAccountList => {
                            this.depositCollectiveAccountsList = depositCollectiveAccountList;
                            this.setDefaultCollectiveAccount();
                        })
                    ));
                }
            }

            if (this.shouldDisplayPmvCcfAccount) {
                actions.push(this.activityPaymentMethodsService.getAccountsList({
                    IsClotured: false,
                    IdTiersForCptCourantOrPMV: this.datasource.third.Id,
                    IsSoldeExerciceEnCours: true,
                    IsCompteCourantFourOrClient: this.isSupplierContext
                }).pipe(
                    tap(accountsList => {
                        this.pmvCcfAccountsList = accountsList;
                        this.setDefaultPmvCcfAccount();
                    })
                ));
            }
        }

        if (this.shouldDisplayBankAccount) {
            actions.push(this.activityPaymentMethodsService.getAdditionalPaymentData(this.selectedPaymentMethod.TypeMoyenPaiement.Code).pipe(
                tap(bankAccountsList => {
                    this.bankAccountsList = bankAccountsList;
                    this.cdr.markForCheck();
                })
            ));
        }

        return forkJoin(actions);
    }

    private handleBankAccountChange(): Observable<any> {
        if (!this.selectedPaymentMethod) {
            return of(null);
        }

        const paymentMethodCode = this.selectedPaymentMethod.TypeMoyenPaiement?.Code;
        const actions: Observable<any>[] = [];

        if (paymentMethodCode !== MoyensPaiement.PorteMonnaieVirtuel) {
            actions.push(this.setDefaultJournal());
        }

        if (paymentMethodCode === MoyensPaiement.PrelevementSEPASurCompte) {
            this.setDefaultMandat();
        }
        return forkJoin(actions);
    }

    private setDefaultPmvCcfAccount(): void {
        if (arrayOnlyOne(this.pmvCcfAccountsList)) {
            this.form.get('pmvCcfAccountId').patchValue(this.pmvCcfAccountsList[0].IdCompte);
        }
    }

    private setDefaultCollectiveAccount(): void {
        const depositAccountId = this.snapshot(SharedPaymentMethodsState.depositAccountId);
        const defaultCollectifAccountId = this.snapshot(SharedPaymentMethodsState.defaultCollectifAccountId);

        if (this.shouldDisplayCollectiveAccount) {
            let account = (arrayOnlyOne(this.collectiveAccountsList)
              ? this.collectiveAccountsList
              : this.collectiveAccountsList.filter(e => e.Id === defaultCollectifAccountId))?.[0]?.Id || null;

            if (this.accountId) {
              account = this.accountId;
            }

            this.form.get('collectiveAccountId').patchValue(account, {emitEvent: false});
        }

        if (this.shouldDisplayCollectiveDepositAccount) {
            const depositAccount = (arrayOnlyOne(this.depositCollectiveAccountsList)
                ? this.depositCollectiveAccountsList
                : this.depositCollectiveAccountsList.filter(e => e.Id === depositAccountId))?.[0]?.Id || null;
            this.form.get('depositAccountId').patchValue(depositAccount, {emitEvent: false});
        }
    }

    private handleIbanChange(): void {
        const paymentMethod = this.selectedPaymentMethod;

        if (paymentMethod.TypeMoyenPaiement.Code === 'PRB') {
            this.setDefaultMandat();
        }
    }

    private initFormSubscriptions(): void {

        this.watch$ = this.form.valueChanges.pipe(
            filter(values => !!values.paymentMethodCode),
            tap(() => this.notifyUpdate(this.updatedOrderLine))
        );

        this.watch$ = this.form.get('paymentMethodCode').valueChanges.pipe(
            // #58852
            // Ajout d'un startWith pour éliminer les cas de champs vide malgré la présence de valeur par défaut à appliquer
            // (compte collectif et/ou journal par exemple)
            startWith(this.form.value.paymentMethodCode),
            this.wrapLoader(() => this.handlePaymentMethodChange(), 'paymentMethodChange')
        );

        this.watch$ = this.form.get('bankAccountId').valueChanges.pipe(
            this.wrapLoader(() => this.handleBankAccountChange(), 'bankAccountChange')
        );

        this.watch$ = this.ibanIdControl.valueChanges.pipe(
            tap(() => this.handleIbanChange())
        );

        this.watch$ = this.form.get('date').valueChanges.pipe(
            tap((date: KalDate) => {
                const criteria = cloneDeep(this.partNumberCriterias);
                criteria.DateEcriture = getISODate(date);
                this.partNumberCriterias = criteria;
            })
        );

        this.watch$ = this.form.get('journalId').valueChanges.pipe(
            tap(journalId => {
                const criteria = cloneDeep(this.partNumberCriterias);
                criteria.IdJournal = journalId;
                this.partNumberCriterias = criteria;
            })
        );

        this.watch$ = this.form.get('pmvCcfAccountId').valueChanges.pipe(
            filter(() => this.hasNotEnoughSoldeOnPmvCcf),
            tap(() => {
                const dialogConfig = new KalDialogConfig<ConfirmDialogData>({
                    title: 'Avertissement ' + (this.isClientContext ? 'porte monnaie virtuel' : 'compte courant fournisseur'),
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    data: {
                        body: 'Solde insuffisant : ' + formatCurrency(this.selectPmvCcfAccount?.Solde, this.locale, this.currency),
                        cancelLabel: 'Fermer'
                    }
                });

                this.dialogService.open(ConfirmDialogComponent, dialogConfig);
            })
        );

    }

    private initStateSubscriptions(): void {
        this.watch$ = this.journalsList$.pipe(
            tap(journalsList => this.journalsList = journalsList || [])
        );

        this.watch$ = this.ibansList$.pipe(
            tap(ibansList => this.ibansList = ibansList || [])
        );

        this.watch$ = this.paymentMethodsList$.pipe(
            tap(payementMethodList => this.paymentMethodsList = payementMethodList || [])
        );

        this.watch$ = this.banksList$.pipe(
            tap(banksList => this.autocompleteBankDataSource = banksList?.map(bank => ({label: bank, value: bank})) || [])
        );

        this.watch$ = this.invoicesList$.pipe(
            tap(invoicesList => {
                this.invoicesList = invoicesList || [];
                if (!this.isDepositContext) {
                    this.form.get('date').setValidators(FormValidator.minDateValidator(this.minDueDate));
                }
            })
        );
    }

    // </editor-fold>

    ngOnInit(): void {
        const tomorrowDate = new KalDate().add(1, 'days');
        this.form = this.fb.group({
            paymentMethodCode: [null, FormValidator.isNotNull],
            date: [new KalDate(), FormValidator.isNotNull],
            amount: [this.maxAmount, [FormValidator.nonNullAmount]],
            bankName: [null],
            bankAccountId: [null, FormValidator.isNotNull],
            journalId: [null, FormValidator.isNotNull],
            depositAccountId: [null],
            chequeNumber: ['', Validators.maxLength(ChequeNumberSize)],
            tiretName: ['', Validators.maxLength(50)],
            chequeDepositDate: [null, [FormValidator.date, FormValidator.minDateValidator(tomorrowDate)]],
            mandatId: [],
            ibanId: [],
            label: ['', [FormValidator.isNotNull, Validators.maxLength(100)]],
            partNumber: [],
            pmvCcfAccountId: [],
            collectiveAccountId: [],
            repaymentSchedule: [],
        });

        if (this.shouldDisplayCollectiveDepositAccount) {
            this.form.get('depositAccountId').setValidators([FormValidator.isNotNull]);
        }

        if (this.datasource) {
            this.watch$ = merge(this.datasource.isInitialized$, this.defaultCollectiveAccountId$).pipe(
                filter(e => !!e),
                tap(() => {
                    this.updateForm();
                    this.cdr.markForCheck(); // nécessaire
                }),
                this.endLoader('datasource')
            );
        } else {
            this.endLoader('datasource');
        }

        this.initFormSubscriptions();
        this.initStateSubscriptions();
    }

}
